import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Drawer, Input, InputNumber, Modal, Spin, Table, Typography } from "antd";
import { FileImageOutlined, FilePdfOutlined, FileTextOutlined, FileUnknownOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../api/axiosInstance";
import { useAuth } from "../../context/AuthContext";
import {
    CardTitle,
    DetailList,
    ListToolbar,
    PageHeader,
    RowActions,
    Section,
    SectionCard,
    StatusTag,
    TabBar,
    fmtDate,
    fmtDateTime as fmt,
    tablePagination,
    useFlatPage,
} from "../../components/ui";
import notify from "../../utils/notify";

const { Text } = Typography;
const { TextArea } = Input;

// application / KYC status -> our status chip
const statusChip = (s) => {
    const v = String(s ?? "").toLowerCase();
    if (v.includes("approved") || v === "3") return "available";
    if (v.includes("rejected") || v === "4") return "cancelled";
    if (v.includes("pending") || v === "1" || v === "2") return "pending";
    return "";
};

const kycChip = (s) => {
    const v = String(s ?? "").toLowerCase();
    if (v.includes("approved") || v === "4") return "available";
    if (v.includes("rejected") || v === "5") return "cancelled";
    if (v.includes("resubmission") || v === "6") return "noshow";
    if (v.includes("review") || v === "3" || v.includes("submitted") || v === "2") return "confirmed";
    return "";
};

const statusLabel = (s) => {
    const v = String(s ?? "").toLowerCase();
    if (v.includes("approved") || v === "3") return "Approved";
    if (v.includes("rejected") || v === "4") return "Rejected";
    if (v.includes("pending") || v === "1" || v === "2") return "Pending";
    return String(s ?? "—");
};

const kycLabel = (s) => {
    const v = String(s ?? "").toLowerCase();
    if (v.includes("approved") || v === "4") return "Approved";
    if (v.includes("rejected") || v === "5") return "Rejected";
    if (v.includes("resubmission") || v === "6") return "Resubmission Req.";
    if (v.includes("review") || v === "3") return "Under Review";
    if (v.includes("submitted") || v === "2") return "Submitted";
    if (v === "notstarted" || v === "1") return "Not Started";
    return String(s);
};

const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    if (ext === "pdf") {
        return <FilePdfOutlined style={{ color: "#ff4d4f", fontSize: 24 }} />;
    }
    if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext)) {
        return <FileImageOutlined style={{ color: "#52c41a", fontSize: 24 }} />;
    }
    if (["doc", "docx"].includes(ext)) {
        return <FileTextOutlined style={{ color: "#2b5797", fontSize: 24 }} />;
    }
    if (["xls", "xlsx", "csv"].includes(ext)) {
        return <FileTextOutlined style={{ color: "#217346", fontSize: 24 }} />;
    }
    if (["txt", "rtf", "md"].includes(ext)) {
        return <FileTextOutlined style={{ color: "#6c757d", fontSize: 24 }} />;
    }
    return <FileUnknownOutlined style={{ color: "#8c8c8c", fontSize: 24 }} />;
};

const isImageFile = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    return ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext);
};

const isPdfFile = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    return ext === "pdf";
};

const revokeObjectUrl = (url) => {
    if (url) {
        window.URL.revokeObjectURL(url);
    }
};

const extractBlobErrorMessage = async (error, fallbackMessage) => {
    try {
        const data = error?.response?.data;
        if (data instanceof Blob) {
            const text = await data.text();
            if (text) {
                try {
                    const parsed = JSON.parse(text);
                    return parsed?.message || fallbackMessage;
                } catch {
                    return text || fallbackMessage;
                }
            }
        }
        return error?.response?.data?.message || error?.message || fallbackMessage;
    } catch {
        return fallbackMessage;
    }
};

const getHotelApplicationDocument = async (hotelId, documentId) => {
    return axiosInstance.get(
        `/HotelAgentApplications/hotels/${hotelId}/documents/${documentId}`,
        {
            responseType: "blob",
            skipAuthRedirect: true,
        }
    );
};

const HotelAgentApplicationsPage = () => {
    useFlatPage();
    const { authData } = useAuth();
    const queryClient = useQueryClient();
    const hotelId = authData?.hotelId;

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [reviewNotes, setReviewNotes] = useState("");
    const [commissionPct, setCommissionPct] = useState(null);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [documentUrl, setDocumentUrl] = useState(null);
    const [loadingDocument, setLoadingDocument] = useState(false);

    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelectedId(null);
        setReviewNotes("");
        setCommissionPct(null);
    };

    const openDocumentViewer = (doc) => {
        setSelectedDocument(doc);
        setViewerOpen(true);
    };

    const closeDocumentViewer = () => {
        setViewerOpen(false);
        setSelectedDocument(null);
        setLoadingDocument(false);
        setDocumentUrl((current) => {
            revokeObjectUrl(current);
            return null;
        });
    };

    useEffect(() => {
        if (!viewerOpen || !selectedDocument?.agentKycDocumentId || !hotelId) return;

        let cancelled = false;
        let createdUrl = null;

        const loadDocument = async () => {
            setLoadingDocument(true);
            setDocumentUrl((current) => {
                revokeObjectUrl(current);
                return null;
            });

            try {
                const response = await getHotelApplicationDocument(
                    hotelId,
                    selectedDocument.agentKycDocumentId
                );
                if (cancelled) return;
                createdUrl = window.URL.createObjectURL(response.data);
                setDocumentUrl(createdUrl);
            } catch (error) {
                if (cancelled) return;
                console.error("Failed to load document:", error);
                const msg = await extractBlobErrorMessage(error, "Failed to load document.");
                notify.error(msg);
            } finally {
                if (!cancelled) {
                    setLoadingDocument(false);
                }
            }
        };

        loadDocument();

        return () => {
            cancelled = true;
            revokeObjectUrl(createdUrl);
        };
    }, [viewerOpen, selectedDocument, hotelId]);

    const hotelQuery = useQuery({
        queryKey: ["hotel-basic", hotelId],
        enabled: !!hotelId,
        queryFn: async () => (await axiosInstance.get(`/hotel/${hotelId}`)).data,
    });

    const appsQuery = useQuery({
        queryKey: ["agent-applications", hotelId],
        enabled: !!hotelId,
        retry: false,
        queryFn: async () => {
            const res = await axiosInstance.get(
                `/HotelAgentApplications/hotels/${hotelId}/applications`
            );
            return Array.isArray(res.data) ? res.data : [];
        },
    });

    const detailQuery = useQuery({
        queryKey: ["agent-application-detail", hotelId, selectedId],
        enabled: !!hotelId && !!selectedId && drawerOpen,
        retry: false,
        queryFn: async () => {
            const res = await axiosInstance.get(
                `/HotelAgentApplications/hotels/${hotelId}/applications/${selectedId}`
            );
            return res.data;
        },
    });

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: ["agent-applications", hotelId] });
        if (selectedId) {
            queryClient.invalidateQueries({
                queryKey: ["agent-application-detail", hotelId, selectedId],
            });
        }
    };

    const approveMutation = useMutation({
        mutationFn: async (payload) =>
            (
                await axiosInstance.post(
                    `/HotelAgentApplications/hotels/${hotelId}/applications/${selectedId}/approve`,
                    payload
                )
            ).data,
        onSuccess: () => {
            notify.success("Agent approved.");
            closeDrawer();
            refresh();
        },
        onError: (e) => notify.error(e?.response?.data?.message || "Approval failed."),
    });

    const rejectMutation = useMutation({
        mutationFn: async (payload) =>
            (
                await axiosInstance.post(
                    `/HotelAgentApplications/hotels/${hotelId}/applications/${selectedId}/reject`,
                    payload
                )
            ).data,
        onSuccess: () => {
            notify.success("Application rejected.");
            closeDrawer();
            refresh();
        },
        onError: (e) => notify.error(e?.response?.data?.message || "Reject failed."),
    });

    const resubmitMutation = useMutation({
        mutationFn: async (payload) =>
            (
                await axiosInstance.post(
                    `/HotelAgentApplications/hotels/${hotelId}/applications/${selectedId}/request-resubmission`,
                    payload
                )
            ).data,
        onSuccess: () => {
            notify.success("Resubmission requested.");
            closeDrawer();
            refresh();
        },
        onError: (e) => notify.error(e?.response?.data?.message || "Resubmission failed."),
    });

    const applications = useMemo(() => appsQuery.data ?? [], [appsQuery.data]);

    const filtered = useMemo(() => {
        return applications.filter((a) => {
            const s = String(a.status ?? "").toLowerCase();
            const matchStatus =
                statusFilter === "all" ||
                (statusFilter === "pending" && (s.includes("pending") || s === "1" || s === "2")) ||
                (statusFilter === "approved" && (s.includes("approved") || s === "3")) ||
                (statusFilter === "rejected" && (s.includes("rejected") || s === "4"));

            const q = search.toLowerCase();
            const matchSearch =
                !q ||
                (a.agentName || "").toLowerCase().includes(q) ||
                (a.companyName || "").toLowerCase().includes(q) ||
                (a.phoneNumber || "").toLowerCase().includes(q);

            return matchStatus && matchSearch;
        });
    }, [applications, statusFilter, search]);

    const counts = useMemo(() => {
        const pending = applications.filter((a) => {
            const s = String(a.status ?? "").toLowerCase();
            return s.includes("pending") || s === "1" || s === "2";
        }).length;
        const approved = applications.filter((a) => {
            const s = String(a.status ?? "").toLowerCase();
            return s.includes("approved") || s === "3";
        }).length;
        const rejected = applications.filter((a) => {
            const s = String(a.status ?? "").toLowerCase();
            return s.includes("rejected") || s === "4";
        }).length;
        return { pending, approved, rejected };
    }, [applications]);

    const openDrawer = (id) => {
        setSelectedId(id);
        setReviewNotes("");
        setCommissionPct(hotelQuery.data?.defaultCommissionPercentage ?? null);
        setDrawerOpen(true);
    };

    const detail = detailQuery.data;
    const detailStatus = String(detail?.status ?? "").toLowerCase();
    const isPending = detailStatus.includes("pending") || detailStatus === "1" || detailStatus === "2";
    const isApproved = detailStatus.includes("approved") || detailStatus === "3";
    const isRejected = detailStatus.includes("rejected") || detailStatus === "4";
    const hasActiveAgent = !!hotelQuery.data?.activeAgentProfileId;

    const initials = (name = "") =>
        String(name)
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((x) => x[0])
            .join("")
            .toUpperCase() || "A";

    const tabs = [
        { key: "all", label: "All", count: applications.length },
        { key: "pending", label: "Pending", count: counts.pending },
        { key: "approved", label: "Approved", count: counts.approved },
        { key: "rejected", label: "Rejected", count: counts.rejected },
    ];

    const columns = [
        {
            title: "Agent",
            key: "agent",
            render: (_, row) => (
                <span className="hms-person">
                    <span className="hms-avatar">{initials(row.agentName)}</span>
                    <span className="hms-cell-stack">
                        <span className="hms-cell-main">{row.agentName || "—"}</span>
                        <span className="hms-cell-sub">{row.companyName || "—"}</span>
                    </span>
                </span>
            ),
        },
        {
            title: "Phone",
            dataIndex: "phoneNumber",
            render: (v) => v || <span className="hms-muted">—</span>,
        },
        {
            title: "Status",
            dataIndex: "status",
            width: 130,
            render: (v) => <StatusTag status={statusChip(v)}>{statusLabel(v)}</StatusTag>,
        },
        {
            title: "KYC",
            key: "kyc",
            width: 170,
            render: (_, row) => {
                const kyc = row.latestKycStatus ?? row.latestKyc?.reviewStatus ?? "NotStarted";
                return <StatusTag status={kycChip(kyc)}>{kycLabel(kyc)}</StatusTag>;
            },
        },
        {
            title: "Applied",
            dataIndex: "appliedAtUtc",
            width: 140,
            render: (v) => fmtDate(v),
            sorter: (a, b) => new Date(a.appliedAtUtc) - new Date(b.appliedAtUtc),
            defaultSortOrder: "descend",
        },
        {
            title: "",
            key: "action",
            align: "right",
            width: 130,
            render: (_, row) => (
                <RowActions
                    actions={[
                        {
                            key: "review",
                            label: "Review application",
                            icon: "view",
                            showLabel: true,
                            onClick: () => openDrawer(row.hotelAgentApplicationId),
                        },
                    ]}
                />
            ),
        },
    ];

    if (!hotelId) {
        return <Alert type="warning" title="No hotel context." showIcon />;
    }

    const kyc = detail?.kycSubmissions?.[0];

    return (
        <div className="hms-page aa-page">
            <PageHeader />

            {appsQuery.isError && (
                <Alert
                    style={{ marginBottom: 12 }}
                    type="warning"
                    showIcon
                    title="Could not load applications."
                    description={appsQuery.error?.response?.data?.message}
                />
            )}

            <SectionCard
                flush
                title={<CardTitle count={applications.length}>Agent applications</CardTitle>}
                extra={
                    <ListToolbar
                        search={{ value: search, onChange: setSearch, placeholder: "Search name, company or phone" }}
                        onRefresh={refresh}
                        loading={appsQuery.isLoading}
                    />
                }
            >
                <TabBar tabs={tabs} value={statusFilter} onChange={setStatusFilter} />

                <Table
                    className="hms-table"
                    rowKey="hotelAgentApplicationId"
                    columns={columns}
                    dataSource={filtered}
                    loading={appsQuery.isLoading}
                    pagination={tablePagination("application")}
                    onRow={(row) => ({ onClick: () => openDrawer(row.hotelAgentApplicationId), style: { cursor: "pointer" } })}
                    locale={{
                        emptyText: (
                            <div className="hms-empty">
                                {applications.length === 0 ? "No agent applications yet." : "No applications match the current filter."}
                            </div>
                        ),
                    }}
                />
            </SectionCard>

            <Drawer
                title="Application details"
                size={640}
                open={drawerOpen}
                onClose={closeDrawer}
                destroyOnHidden
                footer={
                    detail && isPending ? (
                        <div className="hms-form-actions" style={{ marginTop: 0 }}>
                            <Button loading={resubmitMutation.isPending} onClick={() => resubmitMutation.mutate({ reviewNotes })}>
                                Request resubmission
                            </Button>
                            <Button danger loading={rejectMutation.isPending} onClick={() => rejectMutation.mutate({ reviewNotes })}>
                                Reject
                            </Button>
                            <Button
                                type="primary"
                                loading={approveMutation.isPending}
                                disabled={hasActiveAgent}
                                title={hasActiveAgent ? "Clear the current active agent first" : undefined}
                                onClick={() => approveMutation.mutate({ reviewNotes, commissionPercentage: commissionPct })}
                            >
                                Approve
                            </Button>
                        </div>
                    ) : null
                }
            >
                {detailQuery.isLoading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
                        <Spin />
                    </div>
                ) : detail ? (
                    <>
                        <div className="hms-summary">
                            <div>
                                <div className="hms-summary-title">{detail.agent?.displayName || "—"}</div>
                                <div className="hms-summary-sub">{detail.agent?.companyName || "—"}</div>
                            </div>
                            <StatusTag status={statusChip(detail.status)}>{statusLabel(detail.status)}</StatusTag>
                        </div>

                        <DetailList
                            items={[
                                { label: "Phone", value: detail.agent?.phoneNumber },
                                { label: "Address", value: detail.agent?.businessAddress },
                                { label: "License no.", value: detail.agent?.licenseNumber },
                                {
                                    label: "Verified",
                                    value: (
                                        <StatusTag status={detail.agent?.isVerified ? "available" : ""}>
                                            {detail.agent?.isVerified ? "Verified" : "Not verified"}
                                        </StatusTag>
                                    ),
                                },
                            ]}
                        />

                        {kyc && (
                            <Section
                                title="KYC"
                                extra={
                                    <span>
                                        <StatusTag status={kycChip(kyc.reviewStatus)}>{kycLabel(kyc.reviewStatus)}</StatusTag>
                                        <span className="hms-cell-sub" style={{ marginLeft: 8 }}>
                                            Submitted {fmtDate(kyc.submittedAtUtc)}
                                        </span>
                                    </span>
                                }
                            >
                                <DetailList
                                    items={[
                                        { label: "Identity no.", value: kyc.identityNumber },
                                        { label: "Business reg.", value: kyc.businessRegistrationNumber },
                                        { label: "Tax no.", value: kyc.taxNumber },
                                    ]}
                                />

                                <div className="hms-dsection-head" style={{ marginTop: 16 }}>
                                    <h4>Documents</h4>
                                </div>
                                {kyc.documents?.length > 0 ? (
                                    <ul className="hms-checklist">
                                        {kyc.documents.map((doc) => (
                                            <li key={doc.agentKycDocumentId}>
                                                <span className="hms-person">
                                                    {getFileIcon(doc.fileName)}
                                                    <span className="hms-cell-stack">
                                                        <span className="hms-cell-main">{doc.documentType}</span>
                                                        <span className="hms-cell-sub">
                                                            {doc.fileName} · uploaded {fmtDate(doc.uploadedAtUtc)}
                                                        </span>
                                                    </span>
                                                </span>
                                                <RowActions
                                                    actions={[{ key: "view", label: "View document", icon: "view", showLabel: true, onClick: () => openDocumentViewer(doc) }]}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="hms-empty">No documents uploaded</div>
                                )}
                            </Section>
                        )}

                        {detail.reviewedAtUtc && (
                            <Section title="Review history">
                                <DetailList
                                    items={[
                                        { label: "Applied", value: fmt(detail.appliedAtUtc) },
                                        { label: "Last reviewed", value: fmt(detail.reviewedAtUtc) },
                                        detail.reviewNotes ? { label: "Notes", value: detail.reviewNotes } : null,
                                    ]}
                                />
                            </Section>
                        )}

                        <Section title={isPending ? "Your decision" : "Decision"}>
                            {isApproved && <Alert type="success" showIcon title="This application has been approved." />}
                            {isRejected && <Alert type="error" showIcon title="This application was rejected." />}
                            {isPending && (
                                <>
                                    {hasActiveAgent && (
                                        <Alert
                                            type="warning"
                                            showIcon
                                            style={{ marginBottom: 12 }}
                                            title="The hotel already has an active agent. Clear them before approving a new one."
                                        />
                                    )}
                                    <div className="hms-field">
                                        <label htmlFor="aa-commission">
                                            Commission %{" "}
                                            <Text type="secondary" style={{ fontWeight: 400 }}>
                                                (default {hotelQuery.data?.defaultCommissionPercentage ?? "—"}%)
                                            </Text>
                                        </label>
                                        <InputNumber
                                            id="aa-commission"
                                            min={0}
                                            max={100}
                                            addonAfter="%"
                                            style={{ width: "100%" }}
                                            value={commissionPct}
                                            onChange={setCommissionPct}
                                        />
                                    </div>
                                    <div className="hms-field">
                                        <label htmlFor="aa-notes">Review notes</label>
                                        <TextArea
                                            id="aa-notes"
                                            rows={3}
                                            value={reviewNotes}
                                            onChange={(e) => setReviewNotes(e.target.value)}
                                            placeholder="Optional"
                                        />
                                    </div>
                                </>
                            )}
                        </Section>
                    </>
                ) : (
                    <div className="hms-empty">Application not found.</div>
                )}
            </Drawer>

            <Modal
                title={selectedDocument?.documentType || "Document viewer"}
                open={viewerOpen}
                onCancel={closeDocumentViewer}
                footer={[
                    <Button key="close" onClick={closeDocumentViewer}>
                        Close
                    </Button>,
                    <Button
                        key="download"
                        type="primary"
                        disabled={!selectedDocument?.agentKycDocumentId || !hotelId}
                        onClick={async () => {
                            if (!selectedDocument?.agentKycDocumentId || !hotelId) return;
                            try {
                                const response = await getHotelApplicationDocument(hotelId, selectedDocument.agentKycDocumentId);
                                const url = window.URL.createObjectURL(response.data);
                                const link = document.createElement("a");
                                link.href = url;
                                link.download = selectedDocument.fileName || "document";
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                notify.success("Download started");
                            } catch (error) {
                                console.error("Download error:", error);
                                const msg = await extractBlobErrorMessage(error, "Failed to download file.");
                                notify.error(msg);
                            }
                        }}
                    >
                        Download
                    </Button>,
                ]}
                width={900}
                destroyOnHidden
            >
                {loadingDocument ? (
                    <div style={{ textAlign: "center", padding: 48 }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>Loading document...</div>
                    </div>
                ) : selectedDocument && documentUrl ? (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
                        {isImageFile(selectedDocument.fileName) ? (
                            <img src={documentUrl} alt={selectedDocument.fileName} style={{ maxWidth: "100%", maxHeight: "60vh", objectFit: "contain" }} />
                        ) : isPdfFile(selectedDocument.fileName) ? (
                            <iframe src={documentUrl} title={selectedDocument.fileName} style={{ width: "100%", height: "60vh", border: "none" }} />
                        ) : (
                            <div style={{ textAlign: "center" }}>
                                <FileUnknownOutlined style={{ fontSize: 64, color: "var(--hms-text-muted)" }} />
                                <div style={{ marginTop: 16 }}>
                                    <Text type="secondary">Preview not available for this file type.</Text>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="hms-empty">Could not load document.</div>
                )}
            </Modal>
        </div>
    );
};

export default HotelAgentApplicationsPage;
